import { useEffect, useRef } from 'react'
import { FaDownload, FaExternalLinkAlt, FaFileAlt } from 'react-icons/fa'
import Button from '../../../components/atoms/Buttons'
import CloseIcon from '../../../assets/icons/actions/close.svg'
import { downloadFromUrl, formatFileSize } from '../../../utils/fileBrowserApi'
import { kindOf } from '../fileKinds'

export default function FilePreviewModal({ file, url, onClose }) {
  const modalRef = useRef(null)
  const kind = kindOf(file.name)

  useEffect(() => {
    const handleClickOutside = event => {
      if (modalRef.current && !modalRef.current.contains(event.target)) onClose()
    }
    const handleKeyDown = event => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = originalOverflow
    }
  }, [onClose])

  return (
    <div>
      <div className="w-full h-full fixed top-0 left-0 z-40 bg-[#000] opacity-60"></div>

      <div
        ref={modalRef}
        className="w-[92vw] max-w-[960px] max-h-[88vh] rounded-[26px] bg-[#fff] shadow-2xl z-50 fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col font-montserrat overflow-hidden"
      >
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-gray-200">
          <div className="min-w-0">
            <p className="text-base font-semibold text-gray-800 truncate m-0" title={file.name}>
              {file.name}
            </p>
            <p className="text-xs text-gray-500 truncate m-0" title={file.key}>
              {formatFileSize(file.size)} · {file.key}
            </p>
          </div>
          <img
            src={CloseIcon}
            alt="Close"
            onClick={onClose}
            className="w-4 h-4 cursor-pointer shrink-0"
          />
        </div>

        <div className="flex-1 min-h-0 overflow-auto flex items-center justify-center bg-gray-50 p-4">
          {kind === 'image' && (
            <img
              src={url}
              alt={file.name}
              className="max-w-full max-h-[65vh] object-contain rounded-lg"
            />
          )}
          {kind === 'pdf' && (
            <iframe src={url} title={file.name} className="w-full h-[65vh] border-none rounded-lg" />
          )}
          {kind === 'text' && (
            <iframe src={url} title={file.name} className="w-full h-[65vh] border-none rounded-lg bg-white" />
          )}
          {kind === 'video' && (
            <video src={url} controls autoPlay className="max-w-full max-h-[65vh] rounded-lg">
              Your browser cannot play this video.
            </video>
          )}
          {kind === 'audio' && (
            <div className="flex flex-col items-center gap-4 py-10">
              <FaFileAlt className="text-blue-500" size={40} />
              <audio src={url} controls autoPlay className="w-[320px] max-w-full" />
            </div>
          )}
          {kind === 'other' && (
            <div className="flex flex-col items-center gap-3 py-14 text-center">
              <FaFileAlt className="text-blue-500" size={40} />
              <p className="text-sm text-gray-600 m-0">
                No preview available for this file type.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200">
          <Button
            type="button"
            primaryOutline
            AppendIcon={FaExternalLinkAlt}
            onClick={() => window.open(url, '_blank', 'noopener')}
            className="create-saddle-btn !w-auto px-6 cursor-pointer"
          >
            Open in Tab
          </Button>
          <Button
            type="button"
            primary
            AppendIcon={FaDownload}
            onClick={() => downloadFromUrl(url, file.name)}
            className="create-saddle-btn !w-auto px-6 cursor-pointer"
          >
            Download
          </Button>
        </div>
      </div>
    </div>
  )
}
